import Component from '@glimmer/component';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { gt } from 'ember-truth-helpers';
import GeneralCountryFlag from 'lumen/components/general/country-flag';
import GeneralEventName from 'lumen/components/general/event-name';
import { type Day, type Event, Holiday } from 'lumen/models';

import styles from './tooltip-events-and-holidays.module.css';

const NUMBER_OF_EVENTS_AND_HOLIDAYS_SHOWN = 2;

interface HotelPricingAssistantCalendarsDaysTooltipEventsAndHolidaysSignature {
  Args: {
    day: Day;
  };
}

export default class HotelPricingAssistantCalendarsDaysTooltipEventsAndHolidays extends Component<HotelPricingAssistantCalendarsDaysTooltipEventsAndHolidaysSignature> {
  get eventsAndHolidaysData(): {
    eventsAndHolidaysShown: (Event | Holiday)[];
    numberOfEventsAndHolidaysHidden: number;
    totalNumberOfEventsAndHolidays: number;
  } {
    const holidays = [...(this.args.day?.holidays ?? [])];
    const events = [...(this.args.day?.events ?? [])];
    const totalNumberOfEventsAndHolidays = events.length + holidays.length;

    // Show events in the order of their closeness to the hotel i.e sort by distance
    const sortedEvents = events.sort((a, b) => a.distance - b.distance);

    return {
      totalNumberOfEventsAndHolidays,
      eventsAndHolidaysShown: [...holidays, ...sortedEvents].slice(
        0,
        NUMBER_OF_EVENTS_AND_HOLIDAYS_SHOWN,
      ), // We only show two events/holidays in the tooltip and we always show holidays first
      numberOfEventsAndHolidaysHidden:
        totalNumberOfEventsAndHolidays - NUMBER_OF_EVENTS_AND_HOLIDAYS_SHOWN,
    };
  }

  // Type guard function that's used in the template when we loop over eventsAndHolidaysShown in eventsAndHolidaysData.
  // Those items can be either a holiday or event record.
  isHolidayItem(item: Holiday | Event): item is Holiday {
    return item instanceof Holiday;
  }

  <template>
    <table class="default-table {{styles.table}}">
      <tbody>
        {{#each this.eventsAndHolidaysData.eventsAndHolidaysShown as |item|}}
          {{#if (this.isHolidayItem item)}}
            <tr>
              <td class="flex flex-row items-center gap-100">
                {{#each @day.holidayCountries as |holidayCountry|}}
                  <GeneralCountryFlag @countryCode={{holidayCountry}} />
                {{/each}}
                {{item.name}}
              </td>
            </tr>
          {{else}}
            <tr class="flex flex-row items-center">
              <td
                class="{{styles.event-name-column}}
                  flex flex-row items-center gap-100"
              >
                <PrismIcon @name="calendar-accepted" @size="extra-small" />
                <GeneralEventName @event={{item}} />
              </td>

              <td
                class="{{styles.event-distance-column}}
                  flex flex-row items-center gap-100"
              >
                <PrismIcon @name="location-pin" @size="extra-small" />
                {{#if item.distanceFormatted}}
                  {{item.distanceFormatted}}
                {{else}}
                  {{t "general.missing"}}
                {{/if}}
              </td>

              <td
                class="{{styles.event-visitors-column}}
                  flex flex-row items-center gap-100"
              >
                <PrismIcon @name="user-group" @size="extra-small" />
                {{#if item.visitorsFormatted}}
                  {{item.visitorsFormatted}}
                {{else}}
                  {{t "general.missing"}}
                {{/if}}
              </td>
            </tr>
          {{/if}}
        {{/each}}
      </tbody>
    </table>

    {{#if (gt this.eventsAndHolidaysData.totalNumberOfEventsAndHolidays 2)}}
      <p class="text-200--regular pt-300 pr-300 pb-300 pl-300">
        {{t
          "pricing_assistant.day_tooltip.events_holidays.hidden"
          count=this.eventsAndHolidaysData.numberOfEventsAndHolidaysHidden
        }}
      </p>
    {{/if}}
  </template>
}
